import React, { useState, useEffect, useContext, useCallback } from 'react';

// CONTEXT
import { ToastContext } from 'context/ToastContext';

// COMPONENTS
import TableComponent from 'components/common/tableComponent';
import SearchBar from 'components/administration/searchBarComponent';

// ICONS
import { PlusCircle, Edit3, Trash, CalendarDays, CheckCircle, XCircle, Check, X } from 'lucide-react';

// SERVICES
import { getEvents, upsertEvent, deleteEvent } from 'services/eventService';


function ManagementEvents() {
    const { addToast } = useContext(ToastContext); 
    const [events, setEvents] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [error, setError] = useState(null);
    const [confirmingDeleteEventId, setConfirmingDeleteEventId] = useState(null);

    // Ajout d'un état pour forcer la mise à jour des statuts
    const [statusUpdateTrigger, setStatusUpdateTrigger] = useState(0);

    // États pour le formulaire d'ajout/modification
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentEvent, setCurrentEvent] = useState(null);
    const [newEventTitle, setNewEventTitle] = useState('');
    const [newEventStartDate, setNewEventStartDate] = useState('');
    const [newEventEndDate, setNewEventEndDate] = useState('');
    const [newEventDescription, setNewEventDescription] = useState('');
    const [newEventLink, setNewEventLink] = useState('');

    const loadEvents = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await getEvents();
            setEvents(response.data);
            setError(null); // Réinitialiser l'erreur en cas de succès
        } catch (err) {
            setError('Impossible de charger les événements.');
            addToast('Erreur', 'Impossible de charger les événements.', null, null, 'error');
        } finally {
            setIsLoading(false);
        }
    }, [addToast, setEvents, setError, setIsLoading]);

    useEffect(() => {
        loadEvents();
    }, [loadEvents]);

    // Effet pour mettre à jour les statuts toutes les minutes
    useEffect(() => {
        const interval = setInterval(() => {
            setStatusUpdateTrigger(prev => prev + 1);
        }, 1000); // Mise à jour toutes les secondes

        return () => clearInterval(interval);
    }, []);

    const getEventStatus = (startDate, endDate) => {
        const now = new Date();
        const start = new Date(startDate);
        const end = new Date(endDate);

        if (now < start) {
            return { text: 'À venir', variant: 'info', icon: CalendarDays };
        } else if (now >= start && now <= end) {
            return { text: 'En cours', variant: 'success', icon: CheckCircle };
        } else {
            return { text: 'Terminé', variant: 'neutral', icon: XCircle };
        }
    };
    
    const handleOpenModal = (eventToEdit = null) => {
        if (eventToEdit) {
            setCurrentEvent(eventToEdit);
            setNewEventTitle(eventToEdit.title);

            // Formater les dates pour les inputs de type datetime-local
            if (eventToEdit.startDate) {
                const startDate = new Date(eventToEdit.startDate);
                const yearStart = startDate.getFullYear();
                const monthStart = (startDate.getMonth() + 1).toString().padStart(2, '0'); // Les mois sont indexés à partir de 0
                const dayStart = startDate.getDate().toString().padStart(2, '0');
                const hoursStart = startDate.getHours().toString().padStart(2, '0');
                const minutesStart = startDate.getMinutes().toString().padStart(2, '0');
                setNewEventStartDate(`${yearStart}-${monthStart}-${dayStart}T${hoursStart}:${minutesStart}`);
            } else {
                setNewEventStartDate('');
            }

            if (eventToEdit.endDate) {
                const endDate = new Date(eventToEdit.endDate);
                const yearEnd = endDate.getFullYear();
                const monthEnd = (endDate.getMonth() + 1).toString().padStart(2, '0');
                const dayEnd = endDate.getDate().toString().padStart(2, '0');
                const hoursEnd = endDate.getHours().toString().padStart(2, '0');
                const minutesEnd = endDate.getMinutes().toString().padStart(2, '0');
                setNewEventEndDate(`${yearEnd}-${monthEnd}-${dayEnd}T${hoursEnd}:${minutesEnd}`);
            } else {
                setNewEventEndDate('');
            }
            
            setNewEventDescription(eventToEdit.description || '');
            setNewEventLink(eventToEdit.link || '');
        } else {
            setCurrentEvent(null);
            setNewEventTitle('');
            setNewEventStartDate('');
            setNewEventEndDate('');
            setNewEventDescription('');
            setNewEventLink('');
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setCurrentEvent(null);
        // Réinitialiser les champs du formulaire
        setNewEventTitle('');
        setNewEventStartDate('');
        setNewEventEndDate('');
        setNewEventDescription('');
        setNewEventLink('');
    };

    const handleSaveEvent = async (e) => {
        e.preventDefault();
        if (!newEventTitle || !newEventStartDate || !newEventEndDate) {
            addToast('Erreur', 'Veuillez remplir le titre, la date de début et la date de fin.', null, null, 'error');
            return;
        }

        const startDate = new Date(newEventStartDate);
        const endDate = new Date(newEventEndDate);

        if (endDate < startDate) {
            addToast('Erreur', 'La date de fin ne peut pas être antérieure à la date de début.', null, null, 'error');
            return;
        }

        const eventPayload = {
            title: newEventTitle,
            startDate: new Date(newEventStartDate),
            endDate: new Date(newEventEndDate),
            description: newEventDescription,
            link: newEventLink,
        };

        if (currentEvent && currentEvent.id) {
            eventPayload.id = currentEvent.id;
        }

        setIsLoading(true);
        try {
            await upsertEvent(eventPayload);
            if (currentEvent) {
                addToast('Succès', 'Événement mis à jour.', null, null, 'success');
            } else {
                addToast('Succès', 'Événement ajouté.', null, null, 'success');
            }
            await loadEvents();
            handleCloseModal();
        } catch (err) {
            const errorMessage = currentEvent ? 'Impossible de mettre à jour l\'événement.' : 'Impossible d\'ajouter l\'événement.';
            setError(errorMessage);
            addToast('Erreur', errorMessage, null, null, 'error');
        } finally {
            setIsLoading(false);
        }
    };
    
    const handleDeleteEvent = async (eventId) => {
        setConfirmingDeleteEventId(eventId);
    };

    const executeDeleteEvent = async (eventId) => {
        const originalEvents = [...events];
        // Optimistic UI update
        setEvents(prevEvents => prevEvents.filter(event => event.id !== eventId));
        setConfirmingDeleteEventId(null);

        try {
            await deleteEvent(eventId);
            addToast('Succès', 'Événement supprimé.', null, null, 'success');
        } catch (err) { 
            // Rollback en cas d'erreur
            setEvents(originalEvents);
            setError('Impossible de supprimer l\'événement.');
            addToast('Erreur', 'Impossible de supprimer l\'événement.', null, null, 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const cancelDeleteConfirmation = () => {
        setConfirmingDeleteEventId(null);
    };


    const columns = [
        {
            key: 'title',
            label: 'Titre',
            type: 'text',
        },
        {
            key: 'description',
            label: 'Description',
            type: 'text',
        },
        {
            key: 'link',
            label: 'Lien',
            type: 'link',
        },
        {
            key: 'startDate',
            label: 'Date de début',
            type: 'datetime',
        },
        {
            key: 'endDate',
            label: 'Date de fin',
            type: 'datetime',
        },
        {
            key: 'status',
            label: 'Statut',
            type: 'badge',
        },
        {
            key: 'actions',
            label: 'Actions',
            type: 'action',
        },
    ];

    const filteredEvents = events.filter(event => {
        const searchTermLower = searchTerm.toLowerCase();
        const startDate = new Date(event.startDate).toLocaleString('fr-FR');
        const endDate = new Date(event.endDate).toLocaleString('fr-FR');
        const statusInfo = getEventStatus(event.startDate, event.endDate);

        return (
            event.title.toLowerCase().includes(searchTermLower) ||
            (event.description && event.description.toLowerCase().includes(searchTermLower)) ||
            (event.link && event.link.toLowerCase().includes(searchTermLower)) ||
            startDate.includes(searchTermLower) ||
            endDate.includes(searchTermLower) ||
            statusInfo.text.toLowerCase().includes(searchTermLower)
        );
    });

    const tableRows = filteredEvents.map(event => {
        // Forcer le recalcul du statut à chaque mise à jour du trigger
        const statusInfo = getEventStatus(event.startDate, event.endDate);
        return {
            ...event,
            status: {
                text: statusInfo.text,
                variant: statusInfo.variant,
            },
            actions: confirmingDeleteEventId === event.id ? [
                {
                    icon: Check,
                    variant: 'success',
                    onClick: () => executeDeleteEvent(event.id),
                    label: 'Confirmer la suppression'
                },
                {
                    icon: X,
                    variant: 'error',
                    onClick: cancelDeleteConfirmation,
                    label: 'Annuler la suppression'
                }
            ] : [
                {
                    icon: Edit3,
                    variant: 'info',
                    onClick: () => handleOpenModal(event),
                    label: 'Modifier'
                },
                {
                    icon: Trash,
                    variant: 'error',
                    onClick: () => handleDeleteEvent(event.id),
                    label: 'Supprimer'
                }
            ]
        };
    });

    return (
        <div className="h-full flex flex-col">
            <div className="mb-6 flex flex-col sm:flex-row justify-between items-center gap-4">
                <SearchBar
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Rechercher un évènement..."
                />
                <button
                    className="w-full sm:w-auto flex items-center justify-center px-4 py-2 text-blanc rounded-lg shadow-md bg-orange hover:bg-framboise transition-colors ease-in-out duration-300"
                    onClick={() => handleOpenModal()}
                >
                    <PlusCircle className="mr-2" />
                    Nouvel évènement
                </button>
            </div>

            <div className="flex-1 min-h-0">
                {isLoading ? (
                    <div className="flex justify-center items-center h-full bg-blanc dark:bg-dark-secondary rounded-lg shadow-custom">
                        <p className="text-gray-500 dark:text-gray-400">Chargement des événements...</p>
                    </div>
                ) : error ? (
                     <div className="text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900 p-3 rounded-md text-center bg-blanc dark:bg-dark-secondary rounded-lg shadow-custom">{error}</div>
                ) : (
                    <TableComponent
                        columns={columns}
                        rows={tableRows}
                        emptyText="Aucun événement à afficher."
                        defaultSortConfig={{ key: 'startDate', direction: 'descending' }}
                    />
                )}
            </div>

            {/* Modal pour ajouter/modifier un événement */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex justify-center items-center z-50 p-4">
                    <div className="bg-blanc dark:bg-dark-secondary rounded-lg shadow-xl p-6 w-full max-w-lg relative max-h-[90vh] overflow-y-auto">
                        <button
                            onClick={handleCloseModal}
                            className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-blanc"
                        >
                            <XCircle size={24} />
                        </button>
                        <h2 className="text-xl font-semibold text-gray-800 dark:text-blanc mb-6">
                            {currentEvent ? 'Modifier l\'événement' : 'Ajouter un nouvel événement'}
                        </h2>
                        <form autoComplete="off" onSubmit={handleSaveEvent} className="space-y-4">
                            <div>
                                <label htmlFor="eventTitle" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Titre de l'événement <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="eventTitle"
                                    value={newEventTitle}
                                    onChange={(e) => setNewEventTitle(e.target.value)}
                                    className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 dark:bg-dark focus:outline-none focus:ring-orange focus:border-orange dark:focus:ring-orange dark:focus:border-orange"
                                    placeholder="Ex: Présentation Q2"
                                    required
                                />
                            </div>
                             <div>
                                <label htmlFor="eventDescription" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Description (facultatif)
                                </label>
                                <textarea
                                    id="eventDescription"
                                    value={newEventDescription}
                                    onChange={(e) => setNewEventDescription(e.target.value)}
                                    rows="3"
                                    className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 dark:bg-dark focus:outline-none focus:ring-orange focus:border-orange dark:focus:ring-orange dark:focus:border-orange"
                                    placeholder="Ajoutez plus de détails sur l'événement..."
                                />
                            </div>
                            <div>
                                <label htmlFor="eventLink" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Lien (facultatif)
                                </label>
                                <input
                                    type="url"
                                    id="eventLink"
                                    value={newEventLink}
                                    onChange={(e) => setNewEventLink(e.target.value)}
                                    className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 dark:bg-dark focus:outline-none focus:ring-orange focus:border-orange dark:focus:ring-orange dark:focus:border-orange"
                                    placeholder="Ex: https://example.com/event-details"
                                />
                            </div>
                            <div>
                                <label htmlFor="eventStartDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Date et heure de début <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="datetime-local"
                                    id="eventStartDate"
                                    value={newEventStartDate}
                                    onChange={(e) => setNewEventStartDate(e.target.value)}
                                    className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 dark:bg-dark focus:outline-none focus:ring-orange focus:border-orange dark:focus:ring-orange dark:focus:border-orange"
                                    required
                                />
                            </div>
                            <div>
                                <label htmlFor="eventEndDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Date et heure de fin <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="datetime-local"
                                    id="eventEndDate"
                                    value={newEventEndDate}
                                    onChange={(e) => setNewEventEndDate(e.target.value)}
                                    className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 dark:bg-dark focus:outline-none focus:ring-orange focus:border-orange dark:focus:ring-orange dark:focus:border-orange"
                                    required
                                />
                            </div>
                            <div className="flex justify-end space-x-3 pt-4">
                                <button
                                    type="button"
                                    onClick={handleCloseModal}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 focus:outline-none"
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 text-sm font-medium text-blanc bg-orange rounded-md hover:bg-framboise focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange dark:focus:ring-offset-dark"
                                    disabled={isLoading}
                                >
                                    {isLoading ? 'Enregistrement...' : (currentEvent ? 'Mettre à jour' : 'Créer l\'événement')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ManagementEvents;